/**
 * IT Dashboard Modal Components
 * Modal dialogs for the IT Operations Dashboard
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { 
  X, 
  Download, 
  Paperclip,
  File,
  TrendingUp,
  ClipboardList,
  AlertTriangle,
  Megaphone,
  Bell
} from 'lucide-react';
import { 
  StatusBadge, 
  PriorityBadge, 
  SourceBadge, 
  SLABadge, 
  AttachmentIcon, 
  formatFileSize 
} from './UIComponents';
import { formatDateOnly, formatDateTimeET } from '../../utils/formatters';

// IT-TASK-HISTORY-001 — completion modal (optional notes) + full task
// detail/lifecycle viewer. Completing NEVER deletes the record.
export const CompleteTaskModal = ({ isOpen, onClose, task, onSubmit }) => {
  const [notes, setNotes] = useState('');
  if (!isOpen || !task) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="complete-task-modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>Complete Task</h3>
          <button className="modal-close" onClick={onClose} data-testid="complete-task-close"><X size={18} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontWeight: 600, marginBottom: 8 }}>{task.title}</p>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Completion notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="What was done, any follow-ups…"
            style={{ width: '100%', marginTop: 6, border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, fontSize: 13 }}
            data-testid="complete-task-notes"
          />
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => { onSubmit(notes); setNotes(''); }} data-testid="complete-task-confirm">
            Mark Completed
          </button>
        </div>
      </div>
    </div>
  );
};

export const TaskDetailModal = ({ isOpen, onClose, task }) => {
  if (!isOpen || !task) return null;
  const fmt = (d) => d ? formatDateTimeET(d) + ' ET' : '—';
  const rows = [
    ['Task', task.title],
    ['Description', task.description || '—'],
    ['Assigned To', task.assigned_to_name || 'Unassigned'],
    ['Created By', task.created_by_name || '—'],
    ['Created', fmt(task.created_at)],
    ['Due Date', task.due_date ? formatDateOnly(task.due_date) : '—'],
    ['Priority', task.priority],
    ['Status', task.status],
  ];
  if (task.status === 'completed') {
    rows.push(['Completed', fmt(task.completed_at)]);
    rows.push(['Completed By', task.completed_by_name || '—']);
    rows.push(['Completion Notes', task.completion_notes || '—']);
  }
  const evLabel = { created: 'Created', status_change: 'Status Changed', reassigned: 'Reassigned', completed: 'Completed' };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="task-detail-modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Task Details</h3>
          <button className="modal-close" onClick={onClose} data-testid="task-detail-close"><X size={18} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13 }} data-testid="task-detail-fields">
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k}>
                  <td style={{ padding: '5px 10px 5px 0', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                  <td style={{ padding: '5px 0' }}>
                    {k === 'Status' ? <StatusBadge status={v} /> : k === 'Priority' ? <PriorityBadge priority={v} /> : String(v)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4 style={{ margin: '14px 0 8px', fontSize: 13, color: '#0f172a' }}>Lifecycle History</h4>
          {(task.history || []).length === 0 ? (
            <p style={{ fontSize: 12, color: '#94a3b8' }}>No lifecycle events recorded (task predates history tracking).</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="task-history-list">
              {[...task.history].reverse().map((h, i) => (
                <li key={i} style={{ borderLeft: '2px solid #cbd5e1', padding: '4px 0 8px 10px', marginLeft: 4 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{evLabel[h.event] || h.event}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}> — {h.by_name} · {fmt(h.at)}</span>
                  {h.detail ? <div style={{ fontSize: 12, color: '#475569' }}>{h.detail}</div> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// Ticket Detail Modal Component (TDM-REDESIGN Jun 2026 — readability pass)
export const TicketDetailModal = ({ isOpen, onClose, ticket, onDownloadAttachment, onPickup, onUpdateStatus }) => {
  if (!isOpen || !ticket) return null;
  
  const completedAttachments = ticket.attachments?.filter(
    a => a.upload_status === 'completed'
  ) || [];

  // Split auto-generated supporting info (downtime linkage) out of the body
  const rawDesc = ticket.description || '';
  const splitIdx = rawDesc.indexOf('Reported via Downtime');
  const mainDesc = (splitIdx > -1 ? rawDesc.slice(0, splitIdx) : rawDesc).trim();
  const supportingInfo = splitIdx > -1 ? rawDesc.slice(splitIdx).trim() : '';

  const createdDisplay = ticket.created_at
    ? formatDateTimeET(ticket.created_at).replace(',', ',').replace(' at ', ' · ') + ' ET'
    : 'N/A';

  const canAct = onUpdateStatus || onPickup;
  const isClosed = ticket.status === 'resolved' || ticket.status === 'closed';
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tdm-modal" onClick={e => e.stopPropagation()} data-testid="ticket-detail-modal">
        <div className="tdm-header">
          <div className="tdm-header-left">
            <h3>Ticket Details</h3>
            <span className="tdm-ticket-badge" data-testid="tdm-ticket-number">{ticket.ticket_number}</span>
          </div>
          <button className="tdm-close" onClick={onClose} data-testid="tdm-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="tdm-body">
          <h4 className="tdm-subject" data-testid="tdm-subject">{ticket.title}</h4>

          <div className="tdm-grid">
            <div className="tdm-field">
              <span className="tdm-label">Status</span>
              <StatusBadge status={ticket.status} />
            </div>
            <div className="tdm-field">
              <span className="tdm-label">Priority</span>
              <PriorityBadge priority={ticket.priority} />
            </div>
            <div className="tdm-field">
              <span className="tdm-label">Source</span>
              <SourceBadge source={ticket.source} isProactive={ticket.is_proactive} />
            </div>
            <div className="tdm-field">
              <span className="tdm-label">Category</span>
              <span className="tdm-value tdm-cap">{ticket.category ? ticket.category.replace(/_/g, ' ') : '—'}</span>
            </div>
            <div className="tdm-field">
              <span className="tdm-label">Created By</span>
              <span className="tdm-value">{ticket.created_by_name || '—'}</span>
            </div>
            <div className="tdm-field">
              <span className="tdm-label">Assigned To</span>
              <span className={`tdm-value ${!ticket.assigned_to_name ? 'tdm-unassigned' : ''}`}>
                {ticket.assigned_to_name || 'Unassigned'}
              </span>
            </div>
            <div className="tdm-field">
              <span className="tdm-label">Created</span>
              <span className="tdm-value">{createdDisplay}</span>
            </div>
            {ticket.sla && (
              <div className="tdm-field">
                <span className="tdm-label">SLA Status</span>
                <SLABadge sla={ticket.sla} />
              </div>
            )}
          </div>

          <div className="tdm-section" data-testid="tdm-description">
            <span className="tdm-section-title">Description</span>
            <p className="tdm-desc-text">{mainDesc || 'No description provided.'}</p>
            {supportingInfo && (
              <div className="tdm-supporting">
                {supportingInfo.split('\n').filter(Boolean).map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            )}
          </div>

          <div className="tdm-section" data-testid="tdm-attachments">
            <span className="tdm-section-title">
              <Paperclip size={13} /> Attachments ({completedAttachments.length})
            </span>
            {completedAttachments.length > 0 ? (
              <div className="tdm-attachment-list">
                {completedAttachments.map((attachment) => (
                  <div key={attachment.id} className="tdm-attachment-row">
                    <AttachmentIcon fileType={attachment.file_type} size={18} />
                    <div className="tdm-attachment-info">
                      <span className="tdm-attachment-name" title={attachment.filename}>{attachment.filename}</span>
                      <span className="tdm-attachment-meta">
                        {formatFileSize(attachment.file_size)} · {attachment.file_type} · Uploaded by {attachment.uploaded_by_name}
                      </span>
                    </div>
                    <button 
                      className="tdm-attachment-download"
                      onClick={() => onDownloadAttachment(ticket.id, attachment.id, attachment.filename)}
                      title="Download"
                    >
                      <Download size={15} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="tdm-attachments-empty">No attachments uploaded</p>
            )}
          </div>
        </div>

        {canAct && !isClosed && (
          <div className="tdm-footer" data-testid="tdm-actions">
            {!ticket.assigned_to && onPickup && (
              <button className="tdm-btn tdm-btn-primary" onClick={() => { onPickup(ticket.id); onClose(); }} data-testid="tdm-pickup-btn">
                Pick Up
              </button>
            )}
            {onUpdateStatus && ticket.status !== 'on_hold' && (
              <button className="tdm-btn" onClick={() => { onUpdateStatus(ticket.id, 'on_hold'); onClose(); }} data-testid="tdm-hold-btn">
                Put On Hold
              </button>
            )}
            {onUpdateStatus && ticket.status === 'on_hold' && (
              <button className="tdm-btn" onClick={() => { onUpdateStatus(ticket.id, 'in_progress'); onClose(); }} data-testid="tdm-resume-btn">
                Resume
              </button>
            )}
            {onUpdateStatus && (
              <button className="tdm-btn tdm-btn-success" onClick={() => { onUpdateStatus(ticket.id, 'resolved'); onClose(); }} data-testid="tdm-resolve-btn">
                Resolve
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Add Task Modal
export const AddTaskModal = ({ isOpen, onClose, onSubmit, technicians }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        title,
        description,
        priority,
        due_date: dueDate || null,
        assigned_to: assignedTo || null
      });
      setTitle('');
      setDescription('');
      setPriority('medium');
      setDueDate('');
      setAssignedTo('');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay avm-overlay" onClick={onClose}>
      <div className="avm-modal avm-compact" onClick={e => e.stopPropagation()} data-testid="add-task-modal">
        <div className="avm-header">
          <div className="avm-header-left">
            <div className="avm-header-icon"><ClipboardList size={17} /></div>
            <div>
              <h3>Add New Task</h3>
              <p>Create and assign an IT operations task</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="avm-close" aria-label="Close"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="avm-form">
          <div className="avm-body">
            <div className="avm-field">
              <label>Task Title <span className="avm-req">*</span></label>
              <input 
                type="text" 
                value={title} 
                onChange={e => setTitle(e.target.value)}
                placeholder="Enter task title"
                required
                data-testid="task-title-input"
              />
            </div>
            <div className="avm-field avm-stack">
              <label>Description</label>
              <textarea 
                value={description} 
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter task description"
                rows={2}
              />
            </div>
            <div className="avm-grid-2 avm-stack">
              <div className="avm-field">
                <label>Priority</label>
                <select value={priority} onChange={e => setPriority(e.target.value)}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="avm-field">
                <label>Due Date</label>
                <input 
                  type="date" 
                  value={dueDate} 
                  onChange={e => setDueDate(e.target.value)}
                />
              </div>
            </div>
            <div className="avm-field avm-stack">
              <label>Assign To</label>
              <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
                <option value="">Unassigned</option>
                {technicians.map(tech => (
                  <option key={tech.id} value={tech.id}>{tech.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="avm-footer">
            <button type="button" onClick={onClose} className="avm-btn-cancel">Cancel</button>
            <button type="submit" className="avm-btn-save" disabled={isSubmitting || !title.trim()} data-testid="task-create-btn">
              {isSubmitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Incident Modal
export const AddIncidentModal = ({ isOpen, onClose, onSubmit, technicians }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [priority, setPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const categories = [
    { value: 'hardware', label: 'Hardware' },
    { value: 'software', label: 'Software' },
    { value: 'network', label: 'Network' },
    { value: 'access', label: 'Access' },
    { value: 'email', label: 'Email' },
    { value: 'printer', label: 'Printer' },
    { value: 'other', label: 'Other' }
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        title,
        description,
        category,
        priority,
        assigned_to: assignedTo || null
      });
      setTitle('');
      setDescription('');
      setCategory('other');
      setPriority('medium');
      setAssignedTo('');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay avm-overlay" onClick={onClose}>
      <div className="avm-modal avm-compact" onClick={e => e.stopPropagation()} data-testid="add-incident-modal">
        <div className="avm-header">
          <div className="avm-header-left">
            <div className="avm-header-icon"><AlertTriangle size={17} /></div>
            <div>
              <h3>Create New Incident</h3>
              <p>Log and route an IT incident for resolution</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="avm-close" aria-label="Close"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="avm-form">
          <div className="avm-body">
            <div className="avm-field">
              <label>Subject <span className="avm-req">*</span></label>
              <input 
                type="text" 
                value={title} 
                onChange={e => setTitle(e.target.value)}
                placeholder="Brief description of the incident"
                required
                minLength={5}
                data-testid="incident-title-input"
              />
            </div>
            <div className="avm-field avm-stack">
              <label>Description <span className="avm-req">*</span></label>
              <textarea 
                value={description} 
                onChange={e => setDescription(e.target.value)}
                placeholder="Detailed description of the incident, steps to reproduce, impact, etc."
                rows={3}
                required
                minLength={10}
              />
            </div>
            <div className="avm-grid-2 avm-stack">
              <div className="avm-field">
                <label>Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)}>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div className="avm-field">
                <label>Priority</label>
                <select value={priority} onChange={e => setPriority(e.target.value)}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div className="avm-field avm-stack">
              <label>Assign To</label>
              <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
                <option value="">Unassigned</option>
                {technicians.map(tech => (
                  <option key={tech.id} value={tech.id}>{tech.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="avm-footer">
            <button type="button" onClick={onClose} className="avm-btn-cancel">Cancel</button>
            <button type="submit" className="avm-btn-save" disabled={isSubmitting || !title.trim() || !description.trim()} data-testid="incident-create-btn">
              {isSubmitting ? 'Creating...' : 'Create Incident'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Announcement Modal (IT Manager only)
export const AddAnnouncementModal = ({ isOpen, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState('normal');
  const [audience, setAudience] = useState('it');
  const [expiresAt, setExpiresAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        title,
        content,
        priority,
        audience,
        expires_at: expiresAt || null
      });
      setTitle('');
      setContent('');
      setPriority('normal');
      setExpiresAt('');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay avm-overlay" onClick={onClose}>
      <div className="avm-modal avm-compact" onClick={e => e.stopPropagation()} data-testid="add-announcement-modal">
        <div className="avm-header">
          <div className="avm-header-left">
            <div className="avm-header-icon"><Megaphone size={17} /></div>
            <div>
              <h3>Create Announcement</h3>
              <p>Share an update with your team</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="avm-close" aria-label="Close"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="avm-form">
          <div className="avm-body">
            <div className="avm-field">
              <label>Title <span className="avm-req">*</span></label>
              <input 
                type="text" 
                value={title} 
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., Quarterly System Maintenance"
                required
                data-testid="announcement-title-input"
              />
            </div>
            <div className="avm-field avm-stack">
              <label>Content <span className="avm-req">*</span></label>
              <textarea 
                value={content} 
                onChange={e => setContent(e.target.value)}
                placeholder="Enter announcement details..."
                rows={3}
                required
                data-testid="announcement-content-input"
              />
            </div>
            <div className="avm-grid-2 avm-stack">
              <div className="avm-field">
                <label>Audience <span className="avm-req">*</span></label>
                <select value={audience} onChange={e => setAudience(e.target.value)} data-testid="announcement-audience-select">
                  <option value="it">IT Team Only</option>
                  <option value="all">All Staff</option>
                </select>
              </div>
              <div className="avm-field">
                <label>Expires At</label>
                <input 
                  type="datetime-local" 
                  value={expiresAt} 
                  onChange={e => setExpiresAt(e.target.value)}
                  data-testid="announcement-expires-input"
                />
              </div>
            </div>
            {audience === 'all' && (
              <div className="avm-audience-note" data-testid="announcement-all-staff-note">
                Will appear on every user&rsquo;s dashboard and in their notification bell.
              </div>
            )}
            <div className="avm-field avm-stack">
              <label>Priority</label>
              <div className="avm-chip-row" role="group" data-testid="announcement-priority-chips">
                {[['low', 'Low'], ['normal', 'Normal'], ['high', 'High'], ['critical', 'Critical']].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    className={`avm-chip avm-chip--${v} ${priority === v ? 'active' : ''}`}
                    onClick={() => setPriority(v)}
                    data-testid={`announcement-priority-${v}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="avm-footer">
            <button type="button" onClick={onClose} className="avm-btn-cancel">Cancel</button>
            <button type="submit" className="avm-btn-save" disabled={isSubmitting || !title.trim() || !content.trim()} data-testid="announcement-create-btn">
              {isSubmitting ? 'Creating...' : 'Create Announcement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Reminder Modal
export const AddReminderModal = ({ isOpen, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        title,
        description,
        due_date: reminderDate
      });
      setTitle('');
      setDescription('');
      setReminderDate('');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay avm-overlay" onClick={onClose}>
      <div className="avm-modal avm-mini" onClick={e => e.stopPropagation()} data-testid="add-reminder-modal">
        <div className="avm-header">
          <div className="avm-header-left">
            <div className="avm-header-icon"><Bell size={17} /></div>
            <div>
              <h3>Add Reminder</h3>
              <p>Schedule something you don&rsquo;t want to miss</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="avm-close" aria-label="Close"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="avm-form">
          <div className="avm-body">
            <div className="avm-field">
              <label>Reminder Title <span className="avm-req">*</span></label>
              <input 
                type="text" 
                value={title} 
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., Renew firewall license"
                required
                data-testid="reminder-title-input"
              />
            </div>
            <div className="avm-field avm-stack">
              <label>Description</label>
              <textarea 
                value={description} 
                onChange={e => setDescription(e.target.value)}
                placeholder="Optional details..."
                rows={2}
                data-testid="reminder-description-input"
              />
            </div>
            <div className="avm-field avm-stack">
              <label>Reminder Date &amp; Time <span className="avm-req">*</span></label>
              <input 
                type="datetime-local" 
                value={reminderDate} 
                onChange={e => setReminderDate(e.target.value)}
                required
                data-testid="reminder-date-input"
              />
            </div>
          </div>
          <div className="avm-footer">
            <button type="button" onClick={onClose} className="avm-btn-cancel">Cancel</button>
            <button type="submit" className="avm-btn-save" disabled={isSubmitting || !title.trim() || !reminderDate} data-testid="reminder-create-btn">
              {isSubmitting ? 'Creating...' : 'Add Reminder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// SLA Compliance Card Component
export const SLAComplianceCard = ({ metrics }) => {
  if (!metrics) return null;

  // Treat missing / non-numeric rates as "no data" instead of falsely showing
  // red 0% (it reads as "catastrophic failure" on an empty dashboard).
  const hasValue = (rate) => rate !== undefined && rate !== null && !Number.isNaN(Number(rate));
  const formatRate = (rate) => (hasValue(rate) ? `${Number(rate).toFixed(0)}%` : '—');
  const getComplianceColor = (rate) => {
    if (!hasValue(rate)) return 'compliance-empty';
    if (rate >= 90) return 'compliance-good';
    if (rate >= 70) return 'compliance-warning';
    return 'compliance-danger';
  };

  const totalTickets = metrics.total_tickets || 0;
  const breachedCount = (metrics.response?.breached || 0) + (metrics.resolution?.breached || 0);

  const stats = [
    { label: 'Overall', rate: metrics.overall_compliance },
    { label: 'Response', rate: metrics.response?.compliance_rate },
    { label: 'Resolution', rate: metrics.resolution?.compliance_rate },
  ];

  return (
    <div className="sla-compliance-card itp-fade-up" data-testid="sla-compliance-card">
      <div className="compliance-header">
        <div className="compliance-header-left">
          <span className="itp-sla-icon"><TrendingUp size={15} /></span>
          <span>SLA Compliance</span>
        </div>
        <span className="itp-sla-window">Last 30 days</span>
      </div>
      <div className="compliance-stats">
        {stats.map((s) => (
          <div key={s.label} className={`compliance-stat ${getComplianceColor(s.rate)}`} data-testid={`sla-stat-${s.label.toLowerCase()}`}>
            <span className="stat-value">{formatRate(s.rate)}</span>
            <span className="stat-label">{s.label}</span>
            <div className="itp-sla-bar" aria-hidden="true">
              <div
                className="itp-sla-bar-fill"
                style={{ width: hasValue(s.rate) ? `${Math.min(Number(s.rate), 100)}%` : '0%' }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="compliance-footer">
        <span data-testid="sla-total-tickets">Total: {totalTickets} tickets</span>
        <span data-testid="sla-breached-count">Breached: {breachedCount}</span>
      </div>
    </div>
  );
};
